import { Spinner, useToast } from '@chakra-ui/react'
import {
  faChevronLeft,
  faShieldHalved,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import SumsubWebSdk from '@sumsub/websdk-react'
import React, { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import PageWrapper from '../../../../components/Pagewrapper'
import Toast from '../../../../components/Toast'
import {
  coreApi,
  useDeclareKycResidenceMutation,
  useGetKycAccessTokenQuery,
  useGetUserQuery,
  useGetVeliKycStepStateQuery,
} from '../../../../redux/services/coreApi'
import type { RootState } from '../../../../redux/store'
import KycResidenceModal from '../Get Started/components/KycResidenceModal'
import { isE2eKycStub } from './e2eKycStub'
import {
  KYB_REQUIREMENTS,
  KYC_REQUIREMENTS,
  type RequirementSection,
} from './kycKybRequirements'

type VerificationStep = 'explainer' | 'widget'

interface SumsubScreenProps {}

/** Payloads from Sumsub onMessage (subset we use). */
interface StepInitiatedPayload {
  idDocSetType?: string
  types?: string[]
}
interface StepCompletedPayload {
  step?: string
  idDocSetType?: string
}

/**
 * The API refused the access token because no country of residence is declared (the pre-KYC country gate,
 * ADR-0015). Matched on the stable error code, never on the localized message.
 */
const isResidenceRequiredError = (error: unknown) =>
  (error as { data?: { code?: string } } | undefined)?.data?.code ===
  'KycResidenceRequired'

const SumsubScreen: React.FC<SumsubScreenProps> = () => {
  const toast = useToast()
  const history = useHistory()
  const dispatch = useDispatch()
  const sessionUser = useSelector((state: RootState) => state.session.user)
  const { data: user } = useGetUserQuery(sessionUser?.id ?? '', {
    skip: !sessionUser?.id,
  })
  const isOrganization = user?.role === 'Organization'

  // The pre-KYC country gate (ADR-0015), guarded here as well as on the Get Started step: this screen is
  // reachable directly by URL (a bookmark, a shared link), and the access token is refused for an
  // individual with no declared residence — so a customer who skips the checklist would otherwise land on
  // a verification screen that cannot open. Same rules as Get Started: organizations (KYB) are not gated,
  // and a Veli client whose Veli verification was reused continues on the imported documents, so the
  // country is irrelevant for them. The step state is a plain read server-side, so re-asking here is cheap.
  const veliOrigin = user?.isVeliOrigin ?? false
  const isKycPending = user?.kycDetails?.kycStatus === 'Pending'
  const { data: veliStepState, isFetching: isCheckingReuse } =
    useGetVeliKycStepStateQuery(undefined, {
      skip: !user || !veliOrigin || !isKycPending,
    })
  // The API has the final say on the gate. If it refuses the token for a missing residence, the gate opens here
  // even when the checks below decided it wasn't needed, so a disagreement between this screen and the server
  // can never leave the customer on a verification screen that won't open.
  const [residenceRequiredByApi, setResidenceRequiredByApi] = useState(false)
  const needsResidence =
    !!user &&
    user.role === 'Individual' &&
    !user.declaredResidenceIso &&
    (residenceRequiredByApi ||
      !(veliOrigin && (isCheckingReuse || veliStepState?.outcome === 'Reused')))
  const [declareKycResidence, { isLoading: isDeclaringResidence }] =
    useDeclareKycResidenceMutation()

  const [verificationStep, setVerificationStep] =
    useState<VerificationStep>('explainer')
  const [orderedSteps, setOrderedSteps] = useState<string[]>([])
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set())
  const [currentStep, setCurrentStep] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)
  // Flips true once Sumsub reports the applicant as fully verified (its
  // "Your profile has been verified" screen). Drives the auto-redirect below.
  const [verified, setVerified] = useState(false)

  const {
    data: kycAccessTokenData,
    isLoading: kycAccessTokenLoading,
    error,
  } = useGetKycAccessTokenQuery(undefined, {
    // In the hermetic E2E stack the stub stands in for the WebSDK, so we never fetch a Sumsub access token —
    // keeping the run fully offline and inert of the KYC access-token endpoint. Nor while the country gate
    // is still open: the token would be refused.
    skip: verificationStep === 'explainer' || isE2eKycStub() || needsResidence,
  })

  const accessTokenExpirationHandler = () => {
    toast({
      position: 'bottom-right',
      render: () => (
        <Toast type="error">
          Your verification session has expired. Please refresh the page to
          continue.
        </Toast>
      ),
    })
  }

  const handleSuccessNavigation = () => {
    dispatch(coreApi.util.invalidateTags(['User']))
    history.push('/app/get-started')
  }

  // Same outcome contract as the Get Started step: 'eligible' commits the residence (the refetched user
  // closes the gate and the customer carries on here), 'blocked' keeps the modal open with its soft-block
  // message, 'error' toasts. Closing the modal without declaring returns to the checklist — there is no
  // way past the gate.
  const handleSubmitResidence = async (
    residenceIso: string
  ): Promise<'eligible' | 'blocked' | 'error'> => {
    try {
      const { eligible } = await declareKycResidence({ residenceIso }).unwrap()
      if (!eligible) return 'blocked'
      dispatch(coreApi.util.invalidateTags(['User']))
      return 'eligible'
    } catch (error) {
      toast({
        position: 'top-right',
        render: () => (
          <Toast type="error">
            We couldn't check your country of residence. Please try again.
          </Toast>
        ),
      })
      return 'error'
    }
  }

  const residenceGate = (
    <KycResidenceModal
      isOpen={needsResidence}
      onClose={() => history.push('/app/get-started')}
      onSubmit={handleSubmitResidence}
      isLoading={isDeclaringResidence}
    />
  )

  const messageHandler = useCallback(
    (
      event: string,
      payload?: {
        reviewResult?: {
          reviewAnswer: 'GREEN' | 'RED'
          reviewRejectType?: string
        }
        reviewReasonCode?: string
        reviewStatus?: string
      } & StepInitiatedPayload &
        StepCompletedPayload
    ) => {
      // Progress events
      if (event === 'idCheck.onStepInitiated' && payload?.idDocSetType) {
        const t = payload.idDocSetType
        setCurrentStep(t)
        setOrderedSteps(prev => (prev.includes(t) ? prev : [...prev, t]))
      } else if (event === 'idCheck.onStepCompleted') {
        const step = payload?.idDocSetType ?? payload?.step
        if (step) {
          setCompletedSteps(prev => new Set([...prev, step]))
          setCurrentStep(null)
        }
      } else if (event === 'idCheck.onApplicantSubmitted') {
        setSubmitted(true)
        setCurrentStep(null)
      } else if (event === 'idCheck.onLivenessCompleted') {
        setOrderedSteps(prev =>
          prev.includes('LIVENESS') ? prev : [...prev, 'LIVENESS']
        )
        setCompletedSteps(prev => new Set([...prev, 'LIVENESS']))
      }

      // Existing status / toasts
      if (event === 'idCheck.onApplicantStatusChanged') {
        const reviewAnswer = payload?.reviewResult?.reviewAnswer
        const isFinal = payload?.reviewStatus === 'completed'

        if (reviewAnswer === 'GREEN' && isFinal) {
          // Fully approved — Sumsub now shows its "Your profile has been
          // verified" screen. Let the redirect effect take the user home.
          setVerified(true)
        } else if (
          reviewAnswer === 'GREEN' &&
          payload?.reviewReasonCode !== 'waitingIndividuals'
        ) {
          toast({
            position: 'bottom-right',
            render: () => (
              <Toast type="success">
                Your verification has been submitted successfully. We will
                review your documents shortly.
              </Toast>
            ),
          })
        } else if (
          payload?.reviewResult?.reviewAnswer === 'RED' &&
          payload?.reviewResult?.reviewRejectType !== 'RETRY'
        ) {
          toast({
            position: 'bottom-right',
            render: () => (
              <Toast type="error">
                Your verification was not successful. Please try again or
                contact support if the issue persists.
              </Toast>
            ),
          })
        }
      }
    },
    [toast]
  )

  const errorHandler = (event: unknown) => {
    toast({
      position: 'bottom-right',
      render: () => (
        <Toast type="error">
          An error occurred in the verification interface. Please try again or
          contact support if the issue persists.
        </Toast>
      ),
    })
  }

  React.useEffect(() => {
    if (!error) return
    // Not a failure: the server wants the country of residence first, so open the gate instead of a dead end.
    // The latch holds while the gate skips the token query; the declared residence closes the gate again.
    if (isResidenceRequiredError(error)) {
      setResidenceRequiredByApi(true)
      return
    }
    toast({
      position: 'bottom-right',
      render: () => (
        <Toast type="error">
          Failed to load the verification interface. Please try again or contact
          support if the issue persists.
        </Toast>
      ),
    })
  }, [error, toast])

  // Once verified, let the user read Sumsub's success screen for a beat, then
  // take them back to Get Started (which reflects the new, approved status).
  React.useEffect(() => {
    if (!verified) return
    const timer = setTimeout(() => {
      dispatch(coreApi.util.invalidateTags(['User']))
      history.push('/app/get-started')
    }, 2000)
    return () => clearTimeout(timer)
  }, [verified, dispatch, history])

  const pageTitle = isOrganization ? 'KYB Verification' : 'KYC Verification'
  const fullRequirements = isOrganization ? KYB_REQUIREMENTS : KYC_REQUIREMENTS

  const requirementCards = (
    <div className="flex flex-col gap-4">
      {fullRequirements.map((section: RequirementSection) => (
        <div
          key={section.title}
          className="card rounded-2xl border border-black/10 shadow-sm"
        >
          <h3 className="text-default text-sm font-semi mb-2">
            {section.title}
          </h3>
          <ul className="list-disc list-inside text-description space-y-1 text-[13px]">
            {section.items.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  )

  // —— Explainer (pre-step) ———
  if (verificationStep === 'explainer') {
    return (
      <PageWrapper
        title={pageTitle}
        fixedWidth
        goBack={() => history.push('/app/get-started')}
      >
        {residenceGate}
        <div className="w-full max-w-2xl mx-auto flex flex-col gap-6">
          <div className="flex items-center gap-3 text-fg-base">
            <FontAwesomeIcon
              icon={faShieldHalved}
              className="text-xl text-brand-blue"
            />
            <h2 className="h2 m-0">What you&apos;ll need</h2>
          </div>
          <p className="text-description">
            {isOrganization
              ? 'To verify your company we need the following details and documents. Have them ready before you start.'
              : 'To verify your identity we need a valid government-issued identity document (passport, driver’s license, or national ID card), a short selfie check, and a few personal details. Have them ready before you start.'}
          </p>
          {/* Mobile: keep requirements one tap away so the action buttons
              stay within reach without scrolling past a wall of text. */}
          <details className="sm:hidden card rounded-2xl border border-black/10 shadow-sm">
            <summary className="cursor-pointer select-none text-default text-sm font-semi">
              See document requirements
            </summary>
            <div className="mt-3">{requirementCards}</div>
          </details>
          {/* sm and up: show the full requirements inline. */}
          <div className="hidden sm:block">{requirementCards}</div>
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              type="button"
              className="button h-[44px] w-full sm:w-auto sm:min-w-[200px] sm:px-8"
              onClick={() => setVerificationStep('widget')}
            >
              Start verification
            </button>
            <button
              type="button"
              className="button-secondary h-[44px] w-full sm:w-auto sm:px-8 gap-2"
              onClick={() => history.push('/app/get-started')}
            >
              <FontAwesomeIcon icon={faChevronLeft} className="text-xs" />
              Back
            </button>
          </div>
        </div>
      </PageWrapper>
    )
  }

  // —— Widget + requirements side by side; the widget grows with its content so
  //    the page (not the iframe) scrolls — no nested scrollbars. ———
  // Sumsub auto-sizes its iframe to fit the content (adaptIframeHeight), so we
  // give the card a comfortable floor and let it expand from there. A min-height
  // keeps short steps (e.g. the success screen) from looking cramped.
  const widgetMinHeight = 720

  return (
    <PageWrapper title={pageTitle}>
      {residenceGate}
      <div className="w-full flex-1 flex flex-col items-center min-h-0 py-6 px-4">
        {/* The widget and the requirements panel are centered as a group on wide
            screens, so neither overlaps the other and the widget can grow wide. */}
        <div className="w-full max-w-6xl flex flex-col xl:flex-row xl:items-start xl:justify-center gap-6">
          {/* Sumsub widget — wide, content-driven height, no inner scroll.
              Background matches the page (bg-bg-base) and we drop the panel
              border/shadow so the widget blends in instead of reading as a
              bright-white card. The white *inside* the iframe (around Sumsub's
              content box) is rendered cross-origin and can only be changed in
              the Sumsub cockpit — see the customization note. */}
          <div
            className="w-full max-w-3xl mx-auto xl:mx-0 flex flex-col justify-center rounded-2xl overflow-hidden bg-bg-base"
            style={{ minHeight: widgetMinHeight }}
          >
            {isE2eKycStub() ? (
              // Hermetic E2E stub: stands in for the live Sumsub WebSDK so the
              // golden path can drive KYC completion offline, with no traffic to
              // Sumsub. Inert outside the E2E host (VITE_NODE_ENV !== 'e2e'),
              // where the real WebSDK mounts below. The KYC status is actually
              // advanced by self-delivering the applicantReviewed GREEN webhook to
              // the API; this control just stands in for the applicant finishing
              // in the widget and returns them to the checklist.
              <div
                data-testid="e2e-kyc-stub"
                className="flex flex-1 flex-col items-center justify-center gap-4 py-12"
              >
                <p className="text-brand-gray-600">
                  E2E verification stub — the live Sumsub WebSDK is not mounted.
                </p>
                <button
                  type="button"
                  data-testid="e2e-kyc-stub-complete"
                  className="button h-[44px] sm:px-8"
                  onClick={handleSuccessNavigation}
                >
                  Complete verification
                </button>
              </div>
            ) : kycAccessTokenLoading ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-4 py-12">
                <Spinner size="xl" color="brand-blue" />
                <p className="text-brand-gray-600">
                  Loading verification interface...
                </p>
              </div>
            ) : !kycAccessTokenData?.accessToken ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-4 py-12">
                <p className="text-brand-gray-600">
                  Unable to load verification interface
                </p>
              </div>
            ) : (
              // No overflow/max-height here: the SDK reports its content height
              // and we let it expand, so the page scrolls instead of the iframe.
              <SumsubWebSdk
                className="w-full"
                accessToken={kycAccessTokenData.accessToken}
                expirationHandler={accessTokenExpirationHandler}
                onMessage={messageHandler}
                onError={errorHandler}
              />
            )}
          </div>

          {/* Helper panel: shown on wide screens only (xl+). On smaller screens
              it's hidden — the requirements were already shown on the explainer
              step, and stacking them here would push the widget down. */}
          <aside
            className="hidden xl:flex xl:w-[300px] xl:flex-shrink-0 self-start flex-col gap-4 rounded-2xl border border-black/10 shadow-sm bg-brand-light-card dark:bg-brand-dark-card p-5 max-h-[calc(100vh-9rem)] overflow-y-auto"
            aria-label="Document requirements and tips"
          >
            <section>
              <h3 className="text-default text-sm font-semi mb-3">
                Document requirements
              </h3>
              <p className="text-description text-xs mb-3">
                Make sure your documents and details meet these so verification
                goes smoothly:
              </p>
              <div className="flex flex-col gap-4">
                {fullRequirements.map((section: RequirementSection) => (
                  <div key={section.title}>
                    <p className="text-description text-xs font-semi mb-1.5">
                      {section.title}
                    </p>
                    <ul className="list-disc list-inside text-description-subtle text-xs space-y-1.5">
                      {section.items.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </section>
          </aside>
        </div>

        {/* Back to Dashboard beneath the widget */}
        <div className="w-full flex justify-center pt-6">
          <button
            type="button"
            className="button h-[44px] w-full max-w-[300px] sm:w-auto sm:px-10"
            onClick={handleSuccessNavigation}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    </PageWrapper>
  )
}

export default SumsubScreen
